import type {
  EntrGroup,
  EntrPhoto,
  EntrSession,
  PhotoState,
  PrepaidPool,
} from '@/domain/types';
import { toV1ContentImageUrl } from '@/kiosk-core/net/contentImageUrl';
import type {
  EntrGroupListResponseDto,
  EntrGroupLookupResponseDto,
  EntrGroupMemberDto,
  EntrGroupSummaryDto,
  EntrTicketCoverageDto,
  EntrTicketTypeCoverageDto,
  EntrUserPortraitDto,
} from './backendEntrApiTypes';
import type { ListGroupsInput, ListGroupsOutput } from './types';

const GROUP_PORTRAIT_ASPECT_RATIO = 1536 / 1024;
const PORTRAIT_ASPECT_RATIO = 832 / 1216;

function externalEntrId(groupId: string): string {
  return `entr-api-${groupId}`;
}

// Dutch tussenvoegsels — when the second token is one of these, the first
// token alone ("van", "de", ...) is meaningless on its own. Keep at least
// the first two tokens so Amsterdam visitors see something usable.
const DUTCH_TUSSENVOEGSELS = new Set([
  'van',
  'de',
  'der',
  'den',
  'ter',
  'ten',
  'het',
  'op',
  'in',
  'aan',
]);

function stripTrailingDigits(token: string): string {
  // Backend sometimes appends a disambiguator like "Robert Kroner 66". Strip
  // it so the visitor sees their first name.
  return token.replace(/\s+\d+$/, '');
}

function firstNameOnly(groupName: string): string {
  const normalized = stripTrailingDigits(groupName.trim().replace(/\s+/g, ' '));
  const tokens = normalized.split(' ');
  const firstToken = tokens[0] ?? normalized;
  const lowerFirstToken = firstToken.toLowerCase();

  if (lowerFirstToken === 'familie' || lowerFirstToken === 'family') {
    return normalized;
  }

  const secondToken = tokens[1]?.toLowerCase();
  if (secondToken && DUTCH_TUSSENVOEGSELS.has(secondToken)) {
    return normalized;
  }

  return firstToken || normalized;
}

function nonNegativeInteger(value: number | null | undefined): number {
  const raw = Number(value ?? 0);
  return Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
}

// Resolve a prepaid pool's already-consumed count from a per-type or flat
// payload: an explicit `printed` (OUR ledger overlay) wins; otherwise
// coverage_left===false means the pool is fully spent, and an absent/left
// signal means nothing consumed. Clamped to [0, prepaid].
function resolveUsedCount(
  prepaid: number,
  printed: number | null | undefined,
  coverageLeft: boolean | null | undefined
): number {
  const used =
    printed === null || printed === undefined
      ? coverageLeft === false
        ? prepaid
        : 0
      : nonNegativeInteger(printed);
  return Math.min(prepaid, used);
}

// Derive one prepaid pool (group or portrait) from its per-type coverage,
// mirroring the legacy single-pool used-count logic: an explicit `printed`
// wins; otherwise coverage_left=false means the pool is spent.
function prepaidPoolFromType(
  coverage: EntrTicketTypeCoverageDto | null | undefined
): PrepaidPool {
  if (!coverage) return { prepaid: 0, used: 0 };
  const prepaid = nonNegativeInteger(coverage.tickets);
  const hasCoverage = coverage.has_prepaid_coverage ?? prepaid > 0;
  if (!hasCoverage || prepaid <= 0) return { prepaid: 0, used: 0 };

  return {
    prepaid,
    used: resolveUsedCount(
      prepaid,
      coverage.printed,
      coverage.prepaid_coverage_left
    ),
  };
}

function ticketCoverageToVoucher(
  tickets: EntrTicketCoverageDto | null | undefined
): EntrGroup['voucher'] {
  if (!tickets) return { kind: 'none' };

  // Per-type split takes precedence when it carries signal: independent
  // pools so a group credit can't pay for a portrait or vice versa.
  // Production payloads ship a vestigial ALL-ZERO split next to a real flat
  // prepaid_total (every registered group to date) — an empty split carries
  // no signal, so fall through to the flat pool instead of reading it as
  // "no coverage" and walling prepaid customers behind the card reader.
  if (tickets.group_photo || tickets.portrait_photo) {
    const group = prepaidPoolFromType(tickets.group_photo);
    const portrait = prepaidPoolFromType(tickets.portrait_photo);
    if (group.prepaid > 0 || portrait.prepaid > 0) {
      return { kind: 'prepaid-split', group, portrait };
    }
  }

  const prepaidTotal = nonNegativeInteger(tickets.prepaid_total);
  const hasPrepaidCoverage = tickets.has_prepaid_coverage ?? prepaidTotal > 0;

  if (!hasPrepaidCoverage || prepaidTotal <= 0) return { kind: 'none' };

  // No implicit prepaid: ENTR groups no longer carry a default allowance, so
  // coverage comes ONLY from the counters ENTR actually sends. The flat
  // prepaid_total is a single-portrait quota (their documented meaning); a
  // group photo is covered ONLY by an explicit per-type group_photo.tickets
  // (the real-split branch above). We do NOT grant a free group photo per
  // group — an unpaid group photo is always billed at the group price, never
  // a single's — so this pool is portrait-only with an empty group side.
  //
  // Used accounting: real payloads ship the per-type printed ledger in the
  // otherwise-vestigial split object (tickets=0, printed populated); consume
  // the portrait pool from there, falling back to the flat photos_printed on
  // legacy flat-only payloads.
  const portraitUsed = resolveUsedCount(
    prepaidTotal,
    tickets.portrait_photo?.printed ?? tickets.photos_printed,
    tickets.portrait_photo?.prepaid_coverage_left ??
      tickets.prepaid_coverage_left
  );

  return {
    kind: 'prepaid-split',
    group: { prepaid: 0, used: 0 },
    portrait: { prepaid: prepaidTotal, used: portraitUsed },
  };
}

function photoStateFor(status: string | null | undefined): PhotoState {
  return String(status ?? '')
    .trim()
    .toLowerCase() === 'ready'
    ? 'ready'
    : 'not-ready';
}

function memberPortraits(member: EntrGroupMemberDto): EntrUserPortraitDto[] {
  return (member.portraits ?? [])
    .filter(
      (portrait) => portrait.slot_index === 0 || portrait.slot_index === 1
    )
    .sort((a, b) => a.slot_index - b.slot_index);
}

// The kiosk prints by image uuid and the print queue refuses a photo without
// one — AFTER the card was captured. A photo it cannot print must never be
// offered for sale, so a ready row missing its uuid is dropped here.
function readyPortraits(member: EntrGroupMemberDto): EntrUserPortraitDto[] {
  const styles = new Set<string>();
  return memberPortraits(member).filter((portrait) => {
    if (
      portrait.status !== 'ready' ||
      !portrait.image_url ||
      !portrait.image_uuid ||
      styles.has(portrait.style_id)
    ) {
      return false;
    }
    styles.add(portrait.style_id);
    return true;
  });
}

function portraitToPhoto(portrait: EntrUserPortraitDto): EntrPhoto {
  const imageUuid = portrait.image_uuid as string;
  return {
    id: imageUuid,
    type: 'portrait',
    // v1 content twin: streamed 200s instead of the v0 full-object buffer
    // (portraits are multi-MB originals rendered into rail cells).
    url: toV1ContentImageUrl(portrait.image_url as string),
    aspectRatio: PORTRAIT_ASPECT_RATIO,
    printImageUuid: imageUuid,
  };
}

function lookupPortraitsToPhotos(dto: EntrGroupLookupResponseDto): EntrPhoto[] {
  return dto.members.flatMap((member) =>
    readyPortraits(member).map(portraitToPhoto)
  );
}

function hasPendingPortraits(dto: EntrGroupLookupResponseDto): boolean {
  return dto.members.some((member) => {
    const portraits = memberPortraits(member);
    return (
      (Boolean(member.uploaded_photo_uuid) && portraits.length === 0) ||
      portraits.some(
        (portrait) =>
          portrait.status === 'queued' || portrait.status === 'generating'
      )
    );
  });
}

function groupPortraitPhoto(
  imageUuid: string | null | undefined,
  url: string
): EntrPhoto[] {
  if (!(imageUuid && url)) return [];
  return [
    {
      id: imageUuid,
      type: 'group',
      url,
      aspectRatio: GROUP_PORTRAIT_ASPECT_RATIO,
      printImageUuid: imageUuid,
    },
  ];
}

function summaryToGroup(dto: EntrGroupSummaryDto): EntrGroup {
  const name = (dto.group_name ?? dto.group_id).trim() || dto.group_id;
  return {
    id: dto.group_id,
    sessionId: dto.session_id,
    externalEntrId: externalEntrId(dto.group_id),
    coverImageUuid: dto.group_portrait_image_uuid ?? undefined,
    name,
    displayName: firstNameOnly(name),
    memberCount: Math.max(1, nonNegativeInteger(dto.member_count)),
    coverUrl: toV1ContentImageUrl(dto.group_portrait_url ?? ''),
    voucher: ticketCoverageToVoucher(dto.tickets),
    photoState: photoStateFor(dto.group_portrait_status),
  };
}

export function mapGroupToSession(group: EntrGroup): EntrSession {
  const entrExternalId = group.externalEntrId ?? externalEntrId(group.id);
  const groupPhotos =
    group.photoState === 'ready'
      ? groupPortraitPhoto(group.coverImageUuid, group.coverUrl)
      : [];

  return {
    id: group.sessionId ?? entrExternalId,
    externalEntrId: entrExternalId,
    source: 'group',
    group,
    groupPhotos,
    portraits: [],
    voucher: group.voucher,
  };
}

export function mapGroupLookupToSession(
  dto: EntrGroupLookupResponseDto,
  ticketCode?: string | null
): EntrSession {
  const group = summaryToGroup(dto);

  const groupPhotos =
    dto.group_portrait_status === 'ready'
      ? groupPortraitPhoto(
          dto.group_portrait_image_uuid,
          toV1ContentImageUrl(dto.group_portrait_url ?? '')
        )
      : [];

  return {
    id: dto.session_id,
    externalEntrId: externalEntrId(dto.group_id),
    source: ticketCode ? 'ticket' : 'group',
    group,
    groupPhotos,
    portraits: lookupPortraitsToPhotos(dto),
    hasPendingPortraits: hasPendingPortraits(dto),
    ...(ticketCode ? { ticketCode } : {}),
    voucher: group.voucher,
  };
}

export function mapGroupListToGroupList(
  dto: EntrGroupListResponseDto,
  input: ListGroupsInput
): ListGroupsOutput {
  const groups = Array.isArray(dto.data) ? dto.data.map(summaryToGroup) : [];
  const meta = dto.meta;
  const requestedPage = Math.max(1, Math.floor(input.page));
  const pageCount = Math.max(1, nonNegativeInteger(meta?.last_page) || 1);
  const currentPage = nonNegativeInteger(meta?.current_page) || requestedPage;
  const page = Math.min(Math.max(1, currentPage), pageCount);

  return { groups, page, pageCount };
}
